import { useDispatch, useSelector } from 'react-redux';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getMarketId } from 'store/publisher/selectors';
import useRetrieveDirectSalesCampaign from './DirectSalesPlannerActions/useRetrieveDirectSalesCampaign';
import { useDirectSalesPricedSolution } from './useDirectSalesPricedSolution';
import { PostSaveCampaign } from './utils/campaignPostSaveRefresh.types';
import { checkAvailability } from './utils/checkDirectSalesAvailabilityAfterSave';
import { PostSaveFlow, getCampaignPostSaveRefreshSettings } from './utils/getCampaignPostSaveRefreshSettings';
import { loadCampaignPolygons } from './utils/loadCampaignPolygons';
import { reloadPricingSummary } from './utils/reloadPricingSummary';
import {
  findSavedLineId,
  selectRefreshedLines,
  storeLoadedCampaign,
  updateStoredDealId,
} from './utils/updateStoredCampaignAfterSave';

export type { PostSaveCampaign };

export type RefreshAfterSaveArgs = {
  saveFlow: PostSaveFlow;
  campaignId?: string;
  resultingCampaignId?: string;
  savedLineName?: string;
  savedLineId?: string;
  activeLineId?: string;
  overrideLineId?: string;
  shouldAwaitAvailability?: boolean;
  onCampaignTransient?: () => void;
  onFetchCampaign?: (campaign: PostSaveCampaign) => void | Promise<void>;
  onPricingSummary?: (campaign: PostSaveCampaign) => void | Promise<void>;
};

const useCampaignPostSaveRefresh = (): ((args: RefreshAfterSaveArgs) => Promise<PostSaveCampaign | null>) => {
  const dispatch = useDispatch();
  const marketId = useSelector(getMarketId);
  const currentCampaignId = useSelector(getDealId);
  const getUpdatedDirectSalesCampaign = useRetrieveDirectSalesCampaign();
  const handlePricedSolution = useDirectSalesPricedSolution();

  return async ({
    saveFlow,
    campaignId,
    resultingCampaignId,
    savedLineName,
    savedLineId,
    activeLineId,
    overrideLineId,
    shouldAwaitAvailability = false,
    onCampaignTransient,
    onFetchCampaign,
    onPricingSummary,
  }: RefreshAfterSaveArgs): Promise<PostSaveCampaign | null> => {
    const settings = getCampaignPostSaveRefreshSettings(saveFlow);
    const campaign = settings.shouldFetchCampaign
      ? await getUpdatedDirectSalesCampaign(campaignId, settings.shouldSyncProductTemplates)
      : null;
    let foundSavedLineId: string | undefined;

    if (campaign) {
      if (settings.updateCampaignInStore === 'always') storeLoadedCampaign(dispatch, campaign);

      updateStoredDealId({
        dispatch,
        settings,
        currentCampaignId,
        resultingCampaignId,
        campaign,
        onCampaignTransient,
      });
      await loadCampaignPolygons({ dispatch, settings, marketId, polygonCampaignId: campaignId || currentCampaignId });

      foundSavedLineId = findSavedLineId({
        savedLineMatch: settings.savedLineMatch,
        campaign,
        savedLineName,
        savedLineId,
      });
      selectRefreshedLines({ dispatch, settings, campaign, foundSavedLineId, activeLineId });

      if (onFetchCampaign) await onFetchCampaign(campaign);

      await reloadPricingSummary({ settings, campaign, marketId, handlePricedSolution });

      if (onPricingSummary) await onPricingSummary(campaign);
    }

    await checkAvailability({ dispatch, settings, foundSavedLineId, overrideLineId, shouldAwaitAvailability });

    return campaign;
  };
};

export default useCampaignPostSaveRefresh;
